import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Project } from 'api/project';
import { FieldOption, StatusGroup, Task, TaskAPI, TaskType } from 'api/tasks';
import { EnabledSourcesConfiguration } from 'api/timesheets/configuration/configuration';
import { Option } from 'components/atoms/select/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import {
  AssigneeField,
  DateField,
  SingleSelectField,
  UserId,
} from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import {
  DateFieldFilterOperationType,
  RelativeDirection,
} from 'components/organisms/tasks/filters/date';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { RootState } from 'data';
import { useUser } from 'hooks/use-user';
import { FieldsStore, StatusField } from 'stores/fields';
import { TimesheetTask } from '../../types';

enum FilterOption {
  Completed = 'completed',
  InProgress = 'in_progress',
  SubmittedLastWeek = 'submitted_last_week',
  AllTasks = 'all_tasks',
}

const AllProjectFilterOption = [
  FilterOption.Completed,
  FilterOption.InProgress,
  FilterOption.SubmittedLastWeek,
];

const ProjectSpecificOption = [
  FilterOption.AllTasks,
  FilterOption.Completed,
  FilterOption.InProgress,
  FilterOption.SubmittedLastWeek,
];

const ALL_PROJECTS_ID = 1;

export interface TaskListItem extends Task {
  selected: boolean;
  disabled: boolean;
}

export const useAddTasksActions = ({
  projects,
  alreadyAddedTasks,
  startDate,
  timesheetEntriesConfig,
}: {
  projects: Project[];
  startDate: Moment;
  alreadyAddedTasks: TimesheetTask[];
  timesheetEntriesConfig: EnabledSourcesConfiguration;
}): {
  projectOptions: Option[];
  projectSelected: Option;
  setProjectSelected: (project: Option) => void;
  filterOptions: FilterOption[];
  selectedFilter: FilterOption;
  onFilterSelect: (filter: FilterOption) => void;
  isTasksLoading: boolean;
  taskList: TaskListItem[];
  selectedTasks: Task[];
  onTaskSelected: (task: TaskListItem, isSelected: boolean) => void;
  selectedAllDisplayingTasks: () => void;
  searchText: string;
  searchTasks: (e) => void;
  clearSelectedTasks: () => void;
  resetStates: () => void;
  clearSearchText: () => void;
} => {
  const {
    user: { userId: currentUserId },
  } = useUser();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const statusField = FieldsStore.getTaskStatusField(
    fieldsStore,
  )[1] as StatusField;
  const projectField = FieldsStore.getTaskFieldByName('project')(
    fieldsStore,
  )[1] as SingleSelectField;
  const assigneeField = FieldsStore.getTaskFieldByName('assignee')(
    fieldsStore,
  )[1] as AssigneeField;
  const completedAt = FieldsStore.getTaskFieldByName('completedAt')(
    fieldsStore,
  )[1] as DateField;
  const isAlreadyAddedTask = useCallback(
    (task: Task) => !!alreadyAddedTasks.find((x) => x.id === task.taskId),
    [alreadyAddedTasks],
  );

  const projectOptions: Option[] = useMemo(() => {
    return [
      {
        label: 'All Projects',
        value: ALL_PROJECTS_ID,
        extra: { type: 'all-options' },
      } as Option,
    ].concat(
      (projects ?? [])?.map((project) => ({
        label: project.projectName,
        value: project.projectId,
        imageUrl: project.customer?.companyLogoUrl,
      })),
    );
  }, [projects]);

  const [filterOptions, setFilterOptions] = useState(AllProjectFilterOption);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[0]);
  const [isFilterSelectedByUser, setIsFilterSelectedByUser] = useState(false);
  const [projectSelected, setProjectSelected] = useState<Option>(
    projectOptions[0],
  );

  const [taskList, setTaskList] = useState<TaskListItem[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  const onFilterSelect = useCallback((filter: FilterOption) => {
    setIsFilterSelectedByUser(true);
    setSelectedFilter(filter);
  }, []);

  const onProjectChange = useCallback(
    (project: Option) => {
      const prevSelectedProject = projectSelected;

      if (project.value === ALL_PROJECTS_ID) {
        setFilterOptions(AllProjectFilterOption);
        if (!AllProjectFilterOption.includes(selectedFilter)) {
          setSelectedFilter(AllProjectFilterOption[0]);
          setIsFilterSelectedByUser(false);
        }
      } else {
        setFilterOptions(ProjectSpecificOption);
        if (
          prevSelectedProject.value === ALL_PROJECTS_ID &&
          !isFilterSelectedByUser
        ) {
          setSelectedFilter(FilterOption.AllTasks);
        }
      }
      setProjectSelected(project);
    },
    [isFilterSelectedByUser, projectSelected, selectedFilter],
  );
  const getFilterConditions = useCallback((): FieldFilter[] => {
    const filterCondition: FieldFilter[] = [];

    if (projectSelected.value !== ALL_PROJECTS_ID) {
      filterCondition.push({
        field: projectField,
        type: SelectFilterOperationType.Is,
        value: Number(projectSelected.value),
      });
    }

    switch (selectedFilter) {
      case FilterOption.Completed:
        filterCondition.push(
          {
            field: assigneeField,
            type: SelectFilterOperationType.IsOneOf,
            value: [UserId(currentUserId)],
          },
          {
            field: completedAt,
            type: DateFieldFilterOperationType.IsLessThan,
            direction: RelativeDirection.Ago,
            days: moment().diff(startDate.subtract(1, 'day'), 'day'),
          },
        );
        break;
      case FilterOption.InProgress:
        filterCondition.push(
          {
            field: assigneeField,
            type: SelectFilterOperationType.IsOneOf,
            value: [UserId(currentUserId)],
          },
          {
            field: statusField,
            type: SelectFilterOperationType.IsOneOf,
            value: (
              statusField.metaData.options as Required<
                FieldOption<StatusGroup>
              >[]
            )
              .filter(
                (x) => x.active === true && x.group === StatusGroup.InProgress,
              )
              .map((x) => x.value),
          },
        );
        break;
      case FilterOption.SubmittedLastWeek:
      default:
    }

    return filterCondition;
  }, [
    projectSelected.value,
    startDate,
    selectedFilter,
    projectField,
    assigneeField,
    currentUserId,
    completedAt,
    statusField,
  ]);
  const [tasks, setTasks] = useState<Task[]>();
  const [isTasksLoading, setIsTasksLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsTasksLoading(true);
    if (selectedFilter === FilterOption.SubmittedLastWeek) {
      const _startDate = startDate.startOf('day');
      const _endDate = startDate.add(6, 'day').endOf('day');

      TaskAPI.searchTasksForTimesheet(
        _startDate.subtract(7, 'day'),
        _endDate.subtract(7, 'day'),
        projectSelected.value !== ALL_PROJECTS_ID
          ? Number(projectSelected.value)
          : undefined,
      )
        .then((x) => setTasks(x))
        .finally(() => setIsTasksLoading(false));

      return;
    }

    TaskAPI.searchTasks({
      type: QueryCombinerType.All,
      criteria: getFilterConditions(),
    })
      .then((x) => setTasks(x))
      .finally(() => setIsTasksLoading(false));
  }, [getFilterConditions, projectSelected.value, selectedFilter, startDate]);

  const tempTaskList: TaskListItem[] = useMemo(() => {
    if (isTasksLoading) {
      return [];
    }

    return (tasks ?? [])
      .filter((task) =>
        task.taskName.toLowerCase().includes(searchText.toLowerCase()),
      )
      .map((task) => {
        const isAlreadyAdded = isAlreadyAddedTask(task);

        return {
          ...task,
          selected:
            !!selectedTasks.find((_task) => task.taskId === _task.taskId) ||
            isAlreadyAdded,
          disabled: isAlreadyAdded,
        };
      });
  }, [isTasksLoading, tasks, searchText, isAlreadyAddedTask, selectedTasks]);

  useEffect(() => {
    if (timesheetEntriesConfig.TASK && timesheetEntriesConfig.MILESTONE) {
      setTaskList(tempTaskList);
    } else if (timesheetEntriesConfig.TASK) {
      const taskListWithoutMilestone = tempTaskList.filter(
        (task) => task.type !== TaskType.Milestone,
      );

      setTaskList(taskListWithoutMilestone);
    } else if (timesheetEntriesConfig.MILESTONE) {
      const taskListWithOnlyMilestone = tempTaskList.filter(
        (task) => task.type === TaskType.Milestone,
      );

      setTaskList(taskListWithOnlyMilestone);
    }
  }, [
    tempTaskList,
    timesheetEntriesConfig.TASK,
    timesheetEntriesConfig.MILESTONE,
  ]);

  const onTaskSelected = (task: TaskListItem, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks([...selectedTasks, { ...task }]);
    } else {
      setSelectedTasks(
        selectedTasks.filter((_task) => _task.taskId !== task.taskId),
      );
    }
  };

  const selectedAllDisplayingTasks = () => {
    const unselectedTasks = taskList.filter((task) => !task.selected);

    if (unselectedTasks.length > 0) {
      setSelectedTasks([
        ...selectedTasks,
        ...unselectedTasks.map((task) => {
          return { ...task };
        }),
      ]);
    } else {
      setSelectedTasks(
        selectedTasks.filter(
          (task) => !taskList.find((_task) => _task.taskId === task.taskId),
        ),
      );
    }
  };

  const searchTasks = (e) => {
    setSearchText(e.target.value);
  };

  const clearSearchText = () => {
    setSearchText('');
  };

  const clearSelectedTasks = () => {
    setSelectedTasks([]);
  };

  const resetStates = () => {
    clearSearchText();
    clearSelectedTasks();
    setFilterOptions(AllProjectFilterOption);
    setSelectedFilter(AllProjectFilterOption[0]);
    setProjectSelected(projectOptions[0]);
  };

  return {
    projectOptions,
    projectSelected,
    setProjectSelected: onProjectChange,
    filterOptions,
    selectedFilter,
    onFilterSelect,
    isTasksLoading,
    taskList,
    selectedTasks,
    onTaskSelected,
    selectedAllDisplayingTasks,
    searchText,
    searchTasks,
    clearSelectedTasks,
    resetStates,
    clearSearchText,
  };
};
